/**
 * "Move To…": pick a folder for some documents (or for a folder). A search
 * narrows the tree to matching folders, each with its parent path; New Folder
 * makes one under the current pick and selects it. A folder can't go into
 * itself, one of its own folders, or anywhere that would nest past four
 * levels; those rows are disabled.
 */
import { Check, Folder, FolderPlus, Inbox, Layers } from "lucide-react";
import { useEffect, useMemo, useState, type FC } from "react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { NavRow } from "@/components/ui/nav-row";

import {
  canAddSubfolder,
  canMoveFolder,
  folderChain,
  folderRows,
  type FolderTree
} from "../folders";
import type { FirmLibraryFolder } from "../types";

export type MoveSubject =
  | { kind: "entries"; ids: string[]; name?: string }
  | { kind: "folder"; folderId: string };

type Props = {
  subject: MoveSubject | null;
  onOpenChange: (open: boolean) => void;
  tree: FolderTree;
  /** Where the subject is now (null: Unfiled / the top level; undefined: mixed). */
  current: string | null | undefined;
  canCreate: boolean;
  onMove: (target: string | null) => Promise<void> | void;
  onCreateFolder: (
    name: string,
    parentId: string | null
  ) => Promise<FirmLibraryFolder | null>;
};

const NEW_FOLDER_NAME = "New Folder";

export const MoveToDialog: FC<Props> = ({
  subject,
  onOpenChange,
  tree,
  current,
  canCreate,
  onMove,
  onCreateFolder
}) => {
  const [target, setTarget] = useState<string | null>(current ?? null);
  const [query, setQuery] = useState("");
  const [naming, setNaming] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!subject) return;
    setTarget(current ?? null);
    setQuery("");
    setNaming(null);
  }, [subject, current]);

  const isFolder = subject?.kind === "folder";
  const rootLabel = isFolder ? "Top Level" : "Unfiled";
  const blocked = (id: string | null) =>
    subject?.kind === "folder" && !canMoveFolder(tree, subject.folderId, id);
  const q = query.trim().toLocaleLowerCase();
  const rows = useMemo(
    () =>
      folderRows(tree, "all").filter(
        ({ folder }) => !q || folder.name.toLocaleLowerCase().includes(q)
      ),
    [tree, q]
  );

  const title = !subject
    ? ""
    : subject.kind === "folder"
    ? `Move “${tree.byId.get(subject.folderId)?.name ?? "Folder"}”`
    : subject.ids.length === 1 && subject.name
    ? `Move “${subject.name}”`
    : `Move ${subject.ids.length} ${
        subject.ids.length === 1 ? "Document" : "Documents"
      }`;
  const targetName = target ? tree.byId.get(target)?.name ?? "" : rootLabel;

  const createFolder = async () => {
    const name = (naming ?? "").trim();
    if (!name) {
      setNaming(null);
      return;
    }
    setBusy(true);
    const made = await onCreateFolder(name, target);
    setBusy(false);
    setNaming(null);
    if (made) setTarget(made.id);
  };

  const move = async () => {
    setBusy(true);
    try {
      await onMove(target);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={subject !== null} onOpenChange={onOpenChange}>
      <DialogContent
        className="grid-cols-1 sm:max-w-md"
        data-testid="move-to-dialog"
      >
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>
            {isFolder
              ? "Pick where it goes. Its documents and subfolders move with it."
              : "Pick a folder. Tags, visas and kind stay as they are."}
          </DialogDescription>
        </DialogHeader>
        <Input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Find a folder…"
          aria-label="Find a Folder"
        />
        <div
          className="-mx-1 flex max-h-70 flex-col overflow-y-auto"
          role="listbox"
          aria-label="Folders"
        >
          {!q ? (
            <NavRow
              icon={isFolder ? Layers : Inbox}
              active={target === null}
              disabled={blocked(null)}
              onClick={() => setTarget(null)}
              trailing={
                target === null ? <Check className="text-accent-seed" /> : null
              }
              role="option"
              aria-selected={target === null}
            >
              {rootLabel}
            </NavRow>
          ) : null}
          {rows.map(({ folder, depth }) => {
            const disabled = blocked(folder.id);
            const parentPath = folderChain(tree, folder.parent_id)
              .map((f) => f.name)
              .join(" / ");
            return (
              <NavRow
                key={folder.id}
                icon={Folder}
                indent={q ? 0 : depth * 16}
                active={target === folder.id}
                disabled={disabled}
                onClick={() => setTarget(folder.id)}
                role="option"
                aria-selected={target === folder.id}
                data-testid="move-to-folder"
                className="disabled:opacity-50"
                trailing={
                  target === folder.id ? (
                    <Check className="text-accent-seed" />
                  ) : q && parentPath ? (
                    <span className="max-w-40 truncate">{parentPath}</span>
                  ) : null
                }
              >
                {folder.name}
              </NavRow>
            );
          })}
          {q && rows.length === 0 ? (
            <p className="px-2 py-2 text-sm text-fg-3">No folder matches.</p>
          ) : null}
          {naming !== null ? (
            <div className="flex items-center gap-2 px-2 py-1">
              <FolderPlus className="size-4 text-fg-3" />
              <Input
                autoFocus
                value={naming}
                onChange={(e) => setNaming(e.target.value)}
                onFocus={(e) => e.currentTarget.select()}
                onKeyDown={(e) => {
                  if (e.key === "Enter") void createFolder();
                  if (e.key === "Escape") setNaming(null);
                }}
                aria-label="New Folder Name"
                className="h-7"
              />
            </div>
          ) : null}
        </div>
        <DialogFooter className="sm:justify-between">
          {canCreate ? (
            <Button
              variant="ghost"
              size="sm"
              disabled={
                busy || naming !== null || !canAddSubfolder(tree, target)
              }
              onClick={() => setNaming(NEW_FOLDER_NAME)}
            >
              <FolderPlus />
              New Folder
            </Button>
          ) : (
            <span />
          )}
          <div className="flex gap-2">
            <Button
              variant="secondary"
              size="sm"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              size="sm"
              disabled={busy || blocked(target) || target === current}
              onClick={() => void move()}
              data-testid="move-to-submit"
            >
              Move to {targetName}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
